import type React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo, type FormEvent, type ChangeEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Pencil,
  Trash2,
  Eye,
  X,
  FileX,
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  Award,
  ArrowUp,
  ArrowDown,
  ChevronsUpDown,
  Info
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { agentsApi, ordersApi, tradesApi, type Agent, type AgentPerformance, type SimulatedMode } from '@/api/agents';

// The form offers the two modes that mean something. The column's default 'auto' is paper too,
// so an 'auto' row is shown — and saved — as Paper.
type FormSimulatedMode = Exclude<SimulatedMode, 'auto'>;

const SIMULATED_OPTIONS: {
  value: FormSimulatedMode;
  label: string;
  description: string;
}[] = [
  {
    value: 'true',
    label: 'Paper',
    description: 'Manual orders are simulated — never spends real money'
  },
  {
    value: 'false',
    label: 'Real',
    description: 'Manual orders are posted to Polymarket with real money'
  }
];

// Paper rows also show their simulated trade count and simulated Total P&L.
function SimulatedBadge({ agent }: { agent: Agent }) {
  if (agent.simulated === 'false') {
    return <Badge variant="default">Real</Badge>;
  }
  if (agent.simTradeCount === 0) {
    return <Badge variant="outline">Paper</Badge>;
  }
  return (
    <Badge variant="secondary">
      Paper {agent.simTradeCount} ·{' '}
      <span className={agent.simPnl > 0 ? 'text-green-500' : agent.simPnl < 0 ? 'text-red-500' : ''}>
        ${agent.simPnl.toFixed(2)}
      </span>
    </Badge>
  );
}

export const Route = createFileRoute('/custom/agents')({
  component: AgentsPage
});

function pnlColorClass(value: number | null): string {
  if (value === null) {
    return 'text-muted-foreground';
  }
  if (value === 0) {
    return 'text-yellow-500';
  }
  return value > 0 ? 'text-green-500' : 'text-red-500';
}

// Shown on the Total/Unrealized/Realized P&L tooltips so it's clear what the figure nets out.
const PNL_FEE_NOTE = 'Includes trading fees.';

// A small (i) icon that reveals `text` on hover. Uses a span trigger so it can sit safely next to
// the header's sort <button> without nesting buttons.
function InfoTip({ text }: { text: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-help text-muted-foreground hover:text-foreground" aria-label="More info">
          <Info className="size-3" />
        </span>
      </TooltipTrigger>
      <TooltipContent>{text}</TooltipContent>
    </Tooltip>
  );
}

function PerfCard({
  label,
  value,
  icon: Icon,
  colorClass,
  tooltip
}: {
  label: string;
  value: string;
  icon: React.ElementType;
  colorClass?: string;
  tooltip?: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div className="rounded-md bg-muted p-2">
          <Icon className={`size-4 ${colorClass ?? 'text-muted-foreground'}`} />
        </div>
        <div>
          <div className="flex items-center gap-1">
            <p className="text-xs text-muted-foreground">{label}</p>
            {tooltip ? <InfoTip text={tooltip} /> : null}
          </div>
          <p className={`text-lg font-bold ${colorClass ?? ''}`}>{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

type SortKey =
  | 'id'
  | 'name'
  | 'simulated'
  | 'numberOfTrades'
  | 'totalPnl'
  | 'profitLoss'
  | 'realizedProfitLoss'
  | 'lastTradeAt'
  | 'active';

type SortDirection = 'asc' | 'desc';

function getSortValue(agent: Agent, key: SortKey): number | string | null {
  switch (key) {
    case 'id':
      return agent.id;
    case 'name':
      return agent.name.toLowerCase();
    case 'simulated':
      // Real agents sort ahead of paper/simulated ones (ascending → Real first).
      return agent.simulated === 'false' ? 0 : 1;
    case 'numberOfTrades':
      return agent.numberOfTrades ?? null;
    case 'totalPnl':
      if (agent.profitLoss === null && agent.realizedProfitLoss === null) {
        return null;
      }
      return (agent.profitLoss ?? 0) + (agent.realizedProfitLoss ?? 0);
    case 'profitLoss':
      return agent.profitLoss;
    case 'realizedProfitLoss':
      return agent.realizedProfitLoss;
    case 'lastTradeAt':
      return agent.lastTradeAt ? Date.parse(agent.lastTradeAt) : null;
    case 'active':
      return agent.active ? 1 : 0;
  }
}

function formatTimestamp(iso: string | null): string {
  if (!iso) {
    return 'Never';
  }
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function SortableHeader({
  label,
  sortKey,
  currentKey,
  direction,
  onSort,
  align = 'left',
  tooltip
}: {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey | null;
  direction: SortDirection;
  onSort: (key: SortKey) => void;
  align?: 'left' | 'right';
  tooltip?: string;
}) {
  const isActive = currentKey === sortKey;
  const Icon = !isActive ? ChevronsUpDown : direction === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th className={`px-4 py-3 font-medium ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <div className={`inline-flex items-center gap-1 ${align === 'right' ? 'flex-row-reverse' : ''}`}>
        <button
          type="button"
          onClick={() => onSort(sortKey)}
          className={`inline-flex items-center gap-1 hover:text-foreground ${
            isActive ? 'text-foreground' : 'text-muted-foreground'
          }`}
        >
          <span>{label}</span>
          <Icon className="size-3" />
        </button>
        {tooltip ? <InfoTip text={tooltip} /> : null}
      </div>
    </th>
  );
}

function AgentsPage() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [viewingId, setViewingId] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('id');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [activeOnly, setActiveOnly] = useState(true);
  const [realOnly, setRealOnly] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    type: 'agent' | 'orders' | 'trades';
    agentId: number;
    agentName: string;
  } | null>(null);

  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    active: boolean;
    simulated: FormSimulatedMode;
  }>({
    name: '',
    description: '',
    active: true,
    simulated: 'true'
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { data: agentsData, isLoading } = useQuery({
    queryKey: ['agents'],
    queryFn: () => agentsApi.list()
  });
  const agents = useMemo(() => agentsData?.data ?? [], [agentsData]);

  const sortedAgents = useMemo(() => {
    const arr = agents.filter((a: Agent) => (!activeOnly || a.active) && (!realOnly || a.simulated === 'false'));
    arr.sort((a: Agent, b: Agent) => {
      const av = getSortValue(a, sortKey);
      const bv = getSortValue(b, sortKey);
      if (av === null && bv === null) {
        return 0;
      }
      if (av === null) {
        return 1;
      }
      if (bv === null) {
        return -1;
      }
      let cmp: number;
      if (typeof av === 'number' && typeof bv === 'number') {
        cmp = av - bv;
      } else {
        cmp = String(av).localeCompare(String(bv));
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [agents, sortKey, sortDirection, activeOnly, realOnly]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const { data: performanceData, isLoading: perfLoading } = useQuery({
    queryKey: ['agent-performance', viewingId],
    queryFn: () => agentsApi.getPerformance(viewingId!),
    enabled: viewingId !== null
  });
  const performance = performanceData?.data as AgentPerformance | null | undefined;

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      active: true,
      simulated: 'true'
    });
    setImageFile(null);
    setImagePreview(null);
    setCurrentImageUrl(null);
  };

  const createMutation = useMutation({
    mutationFn: agentsApi.create,
    onSuccess: () => {
      setSuccess('Agent created successfully');
      setShowForm(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (err: Error) => setError(err.message)
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: number } & Parameters<typeof agentsApi.update>[1]) =>
      agentsApi.update(id, data),
    onSuccess: () => {
      setSuccess('Agent updated successfully');
      setShowForm(false);
      setEditingId(null);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (err: Error) => setError(err.message)
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, active }: { id: number; active: boolean }) => {
      const agent = agents.find((a: Agent) => a.id === id);
      if (!agent) {
        throw new Error('Agent not found');
      }
      return agentsApi.update(id, {
        name: agent.name,
        description: agent.description ?? undefined,
        imageUrl: agent.imageUrl ?? undefined,
        active
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['agents'] }),
    onError: (err: Error) => setError(err.message)
  });

  const deleteMutation = useMutation({
    mutationFn: agentsApi.delete,
    onSuccess: () => {
      setSuccess('Agent deleted');
      setDeleteConfirm(null);
      queryClient.invalidateQueries({ queryKey: ['agents'] });
    },
    onError: (err: Error) => {
      setError(err.message);
      setDeleteConfirm(null);
    }
  });

  const deleteOrdersMutation = useMutation({
    mutationFn: ordersApi.deleteAllByAgent,
    onSuccess: data => {
      setSuccess(`Deleted ${data.count} order(s)`);
      setDeleteConfirm(null);
    },
    onError: (err: Error) => {
      setError(err.message);
      setDeleteConfirm(null);
    }
  });

  const deleteTradesMutation = useMutation({
    mutationFn: tradesApi.deleteAllByAgent,
    onSuccess: data => {
      setSuccess(`Deleted ${data.count} trade(s)`);
      setDeleteConfirm(null);
    },
    onError: (err: Error) => {
      setError(err.message);
      setDeleteConfirm(null);
    }
  });

  const uploadMutation = useMutation({ mutationFn: agentsApi.uploadImage });

  const handleAdd = () => {
    resetForm();
    setShowForm(true);
    setEditingId(null);
    setError('');
    setSuccess('');
  };

  const handleEdit = (agent: Agent) => {
    setFormData({
      name: agent.name,
      description: agent.description ?? '',
      active: agent.active,
      simulated: agent.simulated === 'false' ? 'false' : 'true'
    });
    setImageFile(null);
    setImagePreview(null);
    setCurrentImageUrl(agent.imageUrl);
    setEditingId(agent.id);
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setImageFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const name = formData.name.trim();
    const description = formData.description.trim() || undefined;

    if (!name) {
      setError('Name is required');
      return;
    }
    let imageUrl: string | undefined = currentImageUrl ?? undefined;

    if (imageFile) {
      try {
        const uploadResult = await uploadMutation.mutateAsync(imageFile);
        if (!uploadResult.success) {
          setError('Failed to upload image');
          return;
        }
        imageUrl = uploadResult.imageUrl;
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to upload image');
        return;
      }
    }

    if (editingId !== null) {
      updateMutation.mutate({
        id: editingId,
        name,
        description,
        imageUrl,
        active: formData.active,
        simulated: formData.simulated
      });
    } else {
      createMutation.mutate({
        name,
        description,
        imageUrl,
        active: formData.active,
        simulated: formData.simulated
      });
    }
  };

  const handleConfirmDelete = () => {
    if (!deleteConfirm) {
      return;
    }
    switch (deleteConfirm.type) {
      case 'agent':
        deleteMutation.mutate(deleteConfirm.agentId);
        break;
      case 'orders':
        deleteOrdersMutation.mutate(deleteConfirm.agentId);
        break;
      case 'trades':
        deleteTradesMutation.mutate(deleteConfirm.agentId);
        break;
    }
  };

  const isDeleting = deleteMutation.isPending || deleteOrdersMutation.isPending || deleteTradesMutation.isPending;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Agents</h1>
          <p className="text-sm text-muted-foreground">The trading identities orders and P&amp;L are recorded under</p>
        </div>
        {!showForm && (
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch id="active-only" checked={activeOnly} onCheckedChange={setActiveOnly} />
              <Label htmlFor="active-only" className="cursor-pointer text-sm font-normal">
                Active only
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="real-only" checked={realOnly} onCheckedChange={setRealOnly} />
              <Label htmlFor="real-only" className="cursor-pointer text-sm font-normal">
                Real only
              </Label>
            </div>
            <Button onClick={handleAdd}>
              <Plus className="mr-2 size-4" /> Add Agent
            </Button>
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive-foreground">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-md border border-green-500/50 bg-green-500/10 p-3 text-sm text-green-500">
          {success}
        </div>
      )}

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle>{editingId !== null ? 'Edit Agent' : 'Create Agent'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={e => setFormData(d => ({ ...d, name: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="simulated">Simulated</Label>
                  <Select
                    value={formData.simulated}
                    onValueChange={(v: FormSimulatedMode) => setFormData(d => ({ ...d, simulated: v }))}
                  >
                    <SelectTrigger id="simulated">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SIMULATED_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>
                          <span className="font-medium">{opt.label}</span>
                          <span className="ml-2 text-xs text-muted-foreground">— {opt.description}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  rows={3}
                  value={formData.description}
                  onChange={e => setFormData(d => ({ ...d, description: e.target.value }))}
                  placeholder="Optional description"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="image">Image</Label>
                <Input id="image" type="file" accept="image/*" onChange={handleImageChange} />
                {(imagePreview || currentImageUrl) && (
                  <div className="mt-2">
                    <img
                      src={imagePreview ?? currentImageUrl ?? ''}
                      alt="Preview"
                      className="size-24 rounded-md object-cover border"
                    />
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  id="active"
                  checked={formData.active}
                  onCheckedChange={c => setFormData(d => ({ ...d, active: c }))}
                />
                <Label htmlFor="active">{formData.active ? 'Active' : 'Inactive'}</Label>
              </div>
              <div className="flex gap-2">
                <Button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending || uploadMutation.isPending}
                >
                  {editingId !== null ? 'Update' : 'Create'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                    resetForm();
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : agents.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No agents yet. Click &quot;Add Agent&quot; to create one.
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium"></th>
                <SortableHeader
                  label="ID"
                  sortKey="id"
                  currentKey={sortKey}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Name"
                  sortKey="name"
                  currentKey={sortKey}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Simulated"
                  sortKey="simulated"
                  currentKey={sortKey}
                  direction={sortDirection}
                  onSort={handleSort}
                  tooltip="Applies to orders placed from the Trades Explorer: Paper simulates the fill against the live book, Real posts it to Polymarket. Sideline and Courtside choose paper or real per game when they are armed."
                />
                <SortableHeader
                  label="# Trades"
                  sortKey="numberOfTrades"
                  currentKey={sortKey}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Total P&L"
                  sortKey="totalPnl"
                  currentKey={sortKey}
                  direction={sortDirection}
                  onSort={handleSort}
                  tooltip={PNL_FEE_NOTE}
                />
                <SortableHeader
                  label="Unrealized P&L"
                  sortKey="profitLoss"
                  currentKey={sortKey}
                  direction={sortDirection}
                  onSort={handleSort}
                  tooltip={PNL_FEE_NOTE}
                />
                <SortableHeader
                  label="Realized P&L"
                  sortKey="realizedProfitLoss"
                  currentKey={sortKey}
                  direction={sortDirection}
                  onSort={handleSort}
                  tooltip={PNL_FEE_NOTE}
                />
                <SortableHeader
                  label="Last Trade"
                  sortKey="lastTradeAt"
                  currentKey={sortKey}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Status"
                  sortKey="active"
                  currentKey={sortKey}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedAgents.map((agent: Agent) => (
                <tr key={agent.id} className="border-b transition-colors hover:bg-muted/50">
                  <td className="px-4 py-3">
                    {agent.imageUrl ? (
                      <img src={agent.imageUrl} alt="" className="size-10 rounded-md object-cover" />
                    ) : (
                      <div className="flex size-10 items-center justify-center rounded-md bg-muted text-muted-foreground">
                        —
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{agent.id}</td>
                  <td className="px-4 py-3 font-medium">{agent.name}</td>
                  <td className="px-4 py-3">
                    <SimulatedBadge agent={agent} />
                  </td>
                  <td className="px-4 py-3">{agent.numberOfTrades ?? '—'}</td>
                  {(() => {
                    const totalPnl =
                      agent.profitLoss === null && agent.realizedProfitLoss === null
                        ? null
                        : (agent.profitLoss ?? 0) + (agent.realizedProfitLoss ?? 0);
                    return (
                      <td className={`px-4 py-3 ${pnlColorClass(totalPnl)}`}>
                        {totalPnl === null ? '—' : `$${totalPnl.toFixed(2)}`}
                      </td>
                    );
                  })()}
                  <td className={`px-4 py-3 ${pnlColorClass(agent.profitLoss)}`}>
                    {agent.profitLoss === null ? '—' : `$${agent.profitLoss.toFixed(2)}`}
                  </td>
                  <td className={`px-4 py-3 ${pnlColorClass(agent.realizedProfitLoss)}`}>
                    {agent.realizedProfitLoss === null ? '—' : `$${agent.realizedProfitLoss.toFixed(2)}`}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                    {formatTimestamp(agent.lastTradeAt)}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => toggleActiveMutation.mutate({ id: agent.id, active: !agent.active })}>
                      <Badge variant={agent.active ? 'default' : 'secondary'} className="cursor-pointer">
                        {agent.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8"
                            onClick={() => {
                              setViewingId(agent.id);
                              setShowForm(false);
                            }}
                          >
                            <Eye className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>View Performance</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button variant="ghost" size="icon" className="size-8" onClick={() => handleEdit(agent)}>
                            <Pencil className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Edit</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8 text-orange-500"
                            onClick={() =>
                              setDeleteConfirm({ type: 'orders', agentId: agent.id, agentName: agent.name })
                            }
                          >
                            <FileX className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Delete Orders</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8 text-amber-500"
                            onClick={() =>
                              setDeleteConfirm({ type: 'trades', agentId: agent.id, agentName: agent.name })
                            }
                          >
                            <BarChart3 className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Delete Trades</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8 text-destructive"
                            onClick={() =>
                              setDeleteConfirm({ type: 'agent', agentId: agent.id, agentName: agent.name })
                            }
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Delete Agent</TooltipContent>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {viewingId !== null && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Performance — {agents.find((a: Agent) => a.id === viewingId)?.name}</CardTitle>
            <Button variant="ghost" size="icon" onClick={() => setViewingId(null)}>
              <X className="size-4" />
            </Button>
          </CardHeader>
          <CardContent>
            {perfLoading ? (
              <div className="grid gap-4 md:grid-cols-3">
                {Array.from({ length: 9 }).map((_, i) => (
                  <Skeleton key={i} className="h-20" />
                ))}
              </div>
            ) : performance ? (
              <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4">
                <PerfCard
                  label="Total P&L"
                  value={`$${(performance.profitLoss + performance.realizedProfitLoss).toFixed(2)}`}
                  icon={performance.profitLoss + performance.realizedProfitLoss >= 0 ? TrendingUp : TrendingDown}
                  colorClass={
                    performance.profitLoss + performance.realizedProfitLoss >= 0 ? 'text-green-500' : 'text-red-500'
                  }
                  tooltip={PNL_FEE_NOTE}
                />
                <PerfCard
                  label="Unrealized P&L"
                  value={`$${performance.profitLoss.toFixed(2)}`}
                  icon={performance.profitLoss >= 0 ? TrendingUp : TrendingDown}
                  colorClass={performance.profitLoss >= 0 ? 'text-green-500' : 'text-red-500'}
                  tooltip={PNL_FEE_NOTE}
                />
                <PerfCard
                  label="Realized P&L"
                  value={`$${performance.realizedProfitLoss.toFixed(2)}`}
                  icon={performance.realizedProfitLoss >= 0 ? TrendingUp : TrendingDown}
                  colorClass={performance.realizedProfitLoss >= 0 ? 'text-green-500' : 'text-red-500'}
                  tooltip={PNL_FEE_NOTE}
                />
                <PerfCard
                  label="Positions Value"
                  value={`$${performance.positionsValue.toFixed(2)}`}
                  icon={DollarSign}
                />
                <PerfCard label="Total Trades" value={String(performance.numberOfTrades)} icon={Activity} />
                <PerfCard
                  label="Buy Trades"
                  value={String(performance.numberOfBuyTrades)}
                  icon={TrendingUp}
                  colorClass="text-green-500"
                />
                <PerfCard
                  label="Sell Trades"
                  value={String(performance.numberOfSellTrades)}
                  icon={TrendingDown}
                  colorClass="text-red-500"
                />
                <PerfCard
                  label="Won"
                  value={String(performance.realizedWonTrades)}
                  icon={Award}
                  colorClass="text-green-500"
                />
                <PerfCard
                  label="Lost"
                  value={String(performance.realizedLostTrades)}
                  icon={TrendingDown}
                  colorClass="text-red-500"
                />
                <PerfCard
                  label="Split"
                  value={String(performance.realizedSplitTrades)}
                  icon={Activity}
                  colorClass="text-amber-500"
                />
                <PerfCard
                  label="Total Fees"
                  value={`$${performance.totalFeesPaid.toFixed(2)}`}
                  icon={DollarSign}
                  colorClass="text-red-500"
                />
              </div>
            ) : (
              <p className="py-8 text-center text-muted-foreground">No performance data available.</p>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog
        open={deleteConfirm !== null}
        onOpenChange={open => {
          if (!open) {
            setDeleteConfirm(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {deleteConfirm?.type === 'agent' && 'Delete Agent'}
              {deleteConfirm?.type === 'orders' && 'Delete Orders'}
              {deleteConfirm?.type === 'trades' && 'Delete Trades'}
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete{' '}
              {deleteConfirm?.type === 'agent'
                ? `agent "${deleteConfirm?.agentName}"`
                : `all ${deleteConfirm?.type} for agent "${deleteConfirm?.agentName}"`}
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)} disabled={isDeleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
